"use client";

// shadcn/ui Button Group (MIT), reviewed through shadcn CLI 4.21.0 --view,
// 2026-09-23. Source: https://ui.shadcn.com/docs/components/radix/button-group
// Adaptations: reuse existing cn/Separator/Radix dependencies; logical inline
// corners/borders for RTL; explicit default orientation. No dependency overwrite.
import type { ComponentProps } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/shadcn/separator";

const buttonGroupVariants = cva(
  "flex w-fit items-stretch has-[>[data-slot=button-group]]:gap-2 [&>*]:focus-visible:relative [&>*]:focus-visible:z-10 has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-e-md [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1",
  { variants: { orientation: {
    horizontal: "[&>*:not(:first-child)]:rounded-s-none [&>*:not(:first-child)]:border-s-0 [&>*:not(:last-child)]:rounded-e-none",
    vertical: "flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none",
  } }, defaultVariants: { orientation: "horizontal" } },
);

function ButtonGroup({ className, orientation = "horizontal", ...props }:
  ComponentProps<"div"> & VariantProps<typeof buttonGroupVariants>) {
  return <div role="group" data-slot="button-group" data-orientation={orientation}
    className={cn(buttonGroupVariants({ orientation }), className)} {...props} />;
}

function ButtonGroupText({ className, asChild = false, ...props }: ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "div";
  return <Comp className={cn("flex items-center gap-2 rounded-md border bg-muted px-4 text-sm font-medium shadow-xs [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4", className)} {...props} />;
}

function ButtonGroupSeparator({ className, orientation = "vertical", ...props }: ComponentProps<typeof Separator>) {
  return <Separator data-slot="button-group-separator" orientation={orientation}
    className={cn("relative m-0! self-stretch bg-input data-[orientation=vertical]:h-auto", className)} {...props} />;
}

export { ButtonGroup, ButtonGroupText, ButtonGroupSeparator, buttonGroupVariants };
